import { useCallback, useEffect, useRef } from 'react';

/**
 * Quiet period before a text-entry `FieldInputted` is dispatched. Matches the
 * legacy `TextQuestion` and `useDebouncedFieldEvent` so DSL and non-DSL flows
 * emit the same cadence.
 */
export const FIELD_INPUT_DEBOUNCE_MS = 2000;

/**
 * `fieldType` values whose `onChange` fires per keystroke. Discrete controls
 * (select, radio, checkbox, switch, tile, multi-select) commit once per
 * interaction and dispatch immediately.
 */
const TEXT_ENTRY_FIELD_TYPES: ReadonlySet<string> = new Set([
  'input',
  'text',
  'textarea',
  'email',
  'tel',
  'phone',
  'number',
  'password',
  'url',
  'search',
  'zip',
]);

export function isTextEntryFieldType(fieldType: string | undefined): boolean {
  return (
    fieldType !== undefined &&
    TEXT_ENTRY_FIELD_TYPES.has(fieldType.toLowerCase())
  );
}

export type ScheduleFieldInput = (dispatch: () => void) => void;

/**
 * Trailing debounce for a single field instance. Each `schedule` call replaces
 * the pending dispatch, so only the latest value is emitted once typing stops.
 * A pending dispatch is flushed on unmount so the final value is not dropped
 * when the step advances inside the quiet period.
 */
export function useFieldInputDebounce(): ScheduleFieldInput {
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const pendingRef = useRef<(() => void) | null>(null);

  const flush = useCallback(() => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current);
      timerRef.current = null;
    }
    const pending = pendingRef.current;
    pendingRef.current = null;
    pending?.();
  }, []);

  useEffect(() => flush, [flush]);

  return useCallback(
    (dispatch: () => void) => {
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current);
      }
      pendingRef.current = dispatch;
      timerRef.current = setTimeout(flush, FIELD_INPUT_DEBOUNCE_MS);
    },
    [flush],
  );
}
